/**
 * Utility functions for placeholder validation and manipulation
 */

import {
  PLACEHOLDER_REGEX,
  VALID_DATE_FORMATS,
  VALID_NUMBER_FORMATS,
} from "../constants";
import { ValidationResult, Field, FieldType } from "../types";

/**
 * Validates placeholder syntax with comprehensive checks
 * Supports: {{key}}, {{key|format}}, nested properties (key.subkey)
 */
export const validatePlaceholderSyntax = (placeholder: string): ValidationResult => {
  if (!placeholder || typeof placeholder !== "string") {
    return { valid: false, error: "Placeholder must be a non-empty string", category: "SYNTAX" };
  }

  const trimmed = placeholder.trim();

  // Check for proper {{placeholder}} or {{placeholder|format}} format
  if (!PLACEHOLDER_REGEX.VALID_PATTERN.test(trimmed)) {
    return {
      valid: false,
      error: `Буруу синтакс: "${placeholder}". {{талбарынНэр}} эсвэл {{талбарынНэр|формат}} хэлбэртэй байх ёстой`,
      category: "SYNTAX",
    };
  }

  // Extract content inside braces
  const content = trimmed.replace(/^\{\{|\}\}$/g, "");
  if (!content || content.trim().length === 0) {
    return {
      valid: false,
      error: `Хоосон түлхүүр: "${placeholder}". Түлхүүр дотор талбарын нэр байх ёстой`,
      category: "SYNTAX",
    };
  }

  // Check for pipe syntax (key|format)
  if (content.includes("|")) {
    const parts = content.split("|");
    if (parts.length !== 2) {
      return {
        valid: false,
        error: `Буруу формат синтакс: "${placeholder}". Зөвхөн нэг | тэмдэгт ашиглах боломжтой`,
        category: "INVALID_FORMAT",
      };
    }

    const [key, format] = parts;
    const keyTrimmed = key.trim();
    const formatTrimmed = format.trim();

    if (!keyTrimmed || !formatTrimmed) {
      return {
        valid: false,
        error: `Буруу формат синтакс: "${placeholder}". Түлхүүр болон формат хоёулаа байх ёстой`,
        category: "INVALID_FORMAT",
      };
    }

    // Validate key is a valid identifier (allows dots for nested properties)
    if (!PLACEHOLDER_REGEX.VALID_KEY.test(keyTrimmed)) {
      return {
        valid: false,
        error: `Буруу түлхүүр нэр: "${keyTrimmed}". Зөвхөн үсэг, тоо, цэг (.), доогуур зураас (_) ашиглах боломжтой`,
        category: "INVALID_KEY",
      };
    }
  } else {
    // No format, validate key only
    const keyTrimmed = content.trim();
    if (!PLACEHOLDER_REGEX.VALID_KEY.test(keyTrimmed)) {
      return {
        valid: false,
        error: `Буруу түлхүүр нэр: "${keyTrimmed}". Зөвхөн үсэг, тоо, цэг (.), доогуур зураас (_) ашиглах боломжтой`,
        category: "INVALID_KEY",
      };
    }
  }

  return { valid: true };
};

/**
 * Validates format string for DATE and NUMBER types
 */
export const validateFormat = (format: string, fieldType: FieldType | undefined): ValidationResult => {
  if (!fieldType || fieldType === "TEXT" || fieldType === "BOOL") {
    return { valid: true }; // Format not applicable
  }

  if (fieldType === "DATE") {
    if (!VALID_DATE_FORMATS.includes(format as any)) {
      return {
        valid: false,
        error: `Буруу огнооны формат: "${format}". Зөв формат сонгоно уу`,
        category: "INVALID_FORMAT",
      };
    }
  } else if (fieldType === "NUMBER" || fieldType === "CURRENCY") {
    if (!VALID_NUMBER_FORMATS.includes(format as any)) {
      return {
        valid: false,
        error: `Буруу тооны формат: "${format}". Зөв формат сонгоно уу`,
        category: "INVALID_FORMAT",
      };
    }
  }

  return { valid: true };
};

/**
 * Extracts placeholders from document content controls and body text
 */
export const extractPlaceholdersFromDocument = async (): Promise<string[]> => {
  try {
    const placeholders = await Word.run(async (context) => {
      const ccs = context.document.contentControls;
      context.load(ccs, ["tag", "text"]);
      await context.sync();

      const foundPlaceholders: string[] = [];
      const processedPlaceholders = new Set<string>();

      for (let i = 0; i < ccs.items.length; i++) {
        const cc = ccs.items[i];
        const tag = cc.tag || "";
        const text = cc.text || "";

        if (tag.startsWith("onehr:")) {
          const key = tag.substring(6);
          const placeholder = `{{${key}}}`;
          if (!processedPlaceholders.has(placeholder)) {
            processedPlaceholders.add(placeholder);
            foundPlaceholders.push(placeholder);
          }
        } else if (text.includes("{{") && text.includes("}}")) {
          const matches = text.match(PLACEHOLDER_REGEX.EXTRACT_PLACEHOLDERS);
          if (matches) {
            matches.forEach((placeholder) => {
              if (!processedPlaceholders.has(placeholder)) {
                processedPlaceholders.add(placeholder);
                foundPlaceholders.push(placeholder);
              }
            });
          }
        }
      }

      if (foundPlaceholders.length === 0) {
        const body = context.document.body;
        context.load(body, "text");
        await context.sync();

        if (body.text && body.text.includes("{{") && body.text.includes("}}")) {
          const bodyMatches = body.text.match(PLACEHOLDER_REGEX.EXTRACT_PLACEHOLDERS);
          if (bodyMatches) {
            foundPlaceholders.push(...bodyMatches);
          }
        }
      }

      return foundPlaceholders;
    });
    return placeholders;
  } catch (error) {
    console.error("Error extracting placeholders:", error);
    return [];
  }
};

/**
 * Creates a map of field keys to Field objects for quick lookup
 */
export const createFieldMap = (
  placeholders: Field[],
  dynamicParticipantFields: Field[][]
): Map<string, Field> => {
  const fieldMap = new Map<string, Field>();

  placeholders.forEach((field) => {
    if (field.key) {
      fieldMap.set(field.key, field);
    }
  });

  dynamicParticipantFields.forEach((participantFields) => {
    participantFields.forEach((field) => {
      if (field.key) {
        fieldMap.set(field.key, field);
      }
    });
  });

  return fieldMap;
};

/**
 * Transforms template placeholders to Field format with proper sorting
 */
export const transformTemplatePlaceholders = (
  templatePlaceholders: any[]
): Field[] => {
  return templatePlaceholders
    .map((p: any, index: number) => ({
      label: p.label || p.key,
      key: p.placeholderCode || p.key,
      category: (p.category || "contract").toLowerCase(),
      type: p.type || "TEXT",
      required: p.required || false,
      sort: p.sort !== undefined ? p.sort : index,
    }))
    .sort((a, b) => {
      if (a.sort !== b.sort) {
        return a.sort - b.sort;
      }
      return a.label.localeCompare(b.label, "mn-MN");
    });
};

/**
 * Groups placeholders by category while maintaining sort order
 */
export const groupPlaceholdersByCategory = (
  placeholderFields: Field[]
): Record<string, Field[]> => {
  const categorized: Record<string, Field[]> = {
    contract: [],
    personal: [],
    job: [],
    salary: [],
    company: [],
    request: [],
    system: [],
    decree: [],
  };

  placeholderFields.forEach((field) => {
    const category = field.category || "contract";
    if (categorized[category]) {
      categorized[category].push(field);
    } else {
      categorized.contract.push(field);
    }
  });

  Object.keys(categorized).forEach((category) => {
    categorized[category].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  });

  return categorized;
};

/**
 * Gets the API key from input or environment variables
 */
export const getApiKey = (inputValue: string): string => {
  if (inputValue) {
    return inputValue;
  }

  try {
    return (
      (typeof process !== "undefined" && process.env?.REACT_APP_CONTRACT_ADD_IN_X_API_KEY) ||
      (typeof process !== "undefined" && process.env?.CONTRACT_ADD_IN_X_API_KEY) ||
      "784e94dae7b42c27-7bfeaff15ecc1366-9b414877580a4541-bc1654c2f0199bda"
    );
  } catch (error) {
    return "784e94dae7b42c27-7bfeaff15ecc1366-9b414877580a4541-bc1654c2f0199bda";
  }
};
